import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import EmptyState from '../../components/EmptyState';
import {
  getClaimsSummary,
  listClaims,
  listPayers,
} from '../../services/suiteApi';

const PAGE_SIZE = 25;

function fmtMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return '--';
  return `$${Number(n).toFixed(2)}`;
}

function StatusPill({ status }) {
  const s = (status || 'unknown').toLowerCase();
  return <span className={`dm-pill dm-pill--${s}`}>{s}</span>;
}

export default function ClaimsListPage() {
  usePageTitle('Claims');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [payers, setPayers] = useState([]);
  const [filters, setFilters] = useState({ status: '', payerId: '' });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, p] = await Promise.allSettled([
          getClaimsSummary(),
          listPayers(),
        ]);
        if (cancelled) return;
        if (s.status === 'fulfilled') setSummary(s.value?.data || null);
        if (p.status === 'fulfilled') {
          const v = p.value?.data;
          setPayers(Array.isArray(v) ? v : (v?.items || []));
        }
      } catch (_) { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const resp = await listClaims({
          page,
          pageSize: PAGE_SIZE,
          status: filters.status || undefined,
          payerId: filters.payerId || undefined,
        });
        if (cancelled) return;
        const v = resp?.data;
        setItems(Array.isArray(v) ? v : (v?.items || []));
        setTotal(v?.total ?? (Array.isArray(v) ? v.length : 0));
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, filters.status, filters.payerId]);

  const pageCount = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));

  const tiles = useMemo(() => {
    if (!summary) return [];
    return [
      { label: 'Total claims',    value: summary.TotalClaims ?? summary.totalClaims ?? 0 },
      { label: 'Total charge',    value: fmtMoney(summary.TotalCharge ?? summary.totalCharge) },
      { label: 'Total paid',      value: fmtMoney(summary.TotalPaid ?? summary.totalPaid) },
      { label: 'Open balance',    value: fmtMoney(summary.OpenBalance ?? summary.openBalance) },
    ];
  }, [summary]);

  return (
    <div className="dm-page">
      <header className="dm-page__header">
        <h1 className="dm-page__title">Claims</h1>
        <p className="dm-page__lede">
          Every 837 you have on file. Filter by status or payer; click any row
          to open a claim's full detail, run the scrubber, or kick off an appeal.
        </p>
      </header>

      {tiles.length > 0 && (
        <section className="dm-tile-grid">
          {tiles.map((t) => (
            <div key={t.label} className="dm-tile">
              <div className="dm-tile__label">{t.label}</div>
              <div className="dm-tile__value">{t.value}</div>
            </div>
          ))}
        </section>
      )}

      <section className="dm-card">
        <div className="dm-card__title">
          <span>Claims ({total})</span>
          <div className="dm-filters">
            <label className="dm-filter">
              <span>Status</span>
              <select
                value={filters.status}
                onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, status: e.target.value })); }}
              >
                <option value="">All</option>
                <option value="draft">draft</option>
                <option value="submitted">submitted</option>
                <option value="paid">paid</option>
                <option value="denied">denied</option>
                <option value="closed">closed</option>
              </select>
            </label>
            <label className="dm-filter">
              <span>Payer</span>
              <select
                value={filters.payerId}
                onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, payerId: e.target.value })); }}
              >
                <option value="">All</option>
                {payers.map((p) => (
                  <option key={p.PayerId || p.payerId || p.PayerCode}
                            value={p.PayerId || p.payerId}>
                    {p.PayerName || p.payerName}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {error && <div className="dm-banner dm-banner--error">{error}</div>}

        {loading ? (
          <div className="dm-card__empty">Loading...</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No claims yet"
            body="Upload a denial pack or file a new claim to populate this list."
            ctaLabel="Upload a denial pack"
            ctaTo="/intake/new"
          />
        ) : (
          <>
            <table className="dm-table">
              <thead>
                <tr>
                  <th>External ID</th>
                  <th>Claim #</th>
                  <th>Patient</th>
                  <th>Payer</th>
                  <th>Status</th>
                  <th className="dm-table__num">Charge</th>
                  <th className="dm-table__num">Paid</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const id = c.ClaimId || c.claimId;
                  return (
                    <tr key={id}>
                      <td><Link to={`/claims/${id}`}>
                        {c.ExternalId || c.externalId || id?.slice(0, 8)}
                      </Link></td>
                      <td>{c.ClaimNumber || c.claimNumber || '--'}</td>
                      <td>{c.PatientId || c.patientId || '--'}</td>
                      <td>{c.PayerName || c.payerName || c.PayerId || ''}</td>
                      <td><StatusPill status={c.Status || c.status} /></td>
                      <td className="dm-table__num">{fmtMoney(c.TotalCharge || c.totalCharge)}</td>
                      <td className="dm-table__num">{fmtMoney(c.TotalPaid || c.totalPaid)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {pageCount > 1 && (
              <div className="dm-pager">
                <button type="button" disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Prev
                </button>
                <span>Page {page} of {pageCount}</span>
                <button type="button" disabled={page >= pageCount}
                          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
